import { FileText, ChevronDown, ChevronRight, BookOpen, Lightbulb, Star, Download } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';

interface TechnicalOutlineDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

export default function TechnicalOutlineDisplay({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}: TechnicalOutlineDisplayProps) {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]));

  // Check if this result contains technical outline data
  if (!result || typeof result !== 'object') return null;

  const isTechnicalOutline = result.analysis_id !== undefined ||
                             result.subsections_count !== undefined ||
                             result.full_result !== undefined;

  if (!isTechnicalOutline) return null;

  // Get the full result data
  const fullResult = result.full_result || result;
  const subsections = fullResult.subsections || [];
  const mainInnovation = fullResult.main_innovation || '';

  // Find the main innovation section
  const mainInnovationSection = subsections.find((s: any) => s.is_main_innovation === true);

  const toggleSection = (index: number) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSections(newExpanded);
  };

  const getTechnicalDepthColor = (depth: string) => {
    switch (depth?.toLowerCase()) {
      case 'overview':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'detailed':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'comprehensive':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const exportToDocx = async () => {
    try {
      const children: Paragraph[] = [];

      // Title
      children.push(
        new Paragraph({
          text: "IR Technical Outline",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Main Innovation
      if (mainInnovation) {
        children.push(
          new Paragraph({
            text: "Main Innovation",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 },
          })
        );
        children.push(
          new Paragraph({
            text: mainInnovation,
            spacing: { after: 300 },
          })
        );
      }

      // Subsections
      subsections.forEach((subsection: any, index: number) => {
        // Subsection title
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${index + 1}. ${subsection.subsection}`,
                bold: true,
              }),
              ...(subsection.is_main_innovation ? [
                new TextRun({
                  text: " ★",
                  color: "FFA500",
                })
              ] : []),
            ],
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 300, after: 200 },
          })
        );

        // Technical depth and section focus
        children.push(
          new Paragraph({
            text: `Technical Depth: ${subsection.technical_depth || 'N/A'} | Section Focus: ${subsection.section_focus || 'N/A'}`,
            italics: true,
            spacing: { after: 200 },
          })
        );

        // Content points
        if (subsection.content_points && subsection.content_points.length > 0) {
          children.push(
            new Paragraph({
              text: "Content Points to Cover:",
              bold: true,
              spacing: { before: 100, after: 100 },
            })
          );

          subsection.content_points.forEach((point: string, pointIndex: number) => {
            children.push(
              new Paragraph({
                text: `${pointIndex + 1}. ${point}`,
                spacing: { after: 100 },
                indent: { left: 360 },
              })
            );
          });
        }
      });

      if (subsections.length === 0) {
        children.push(
          new Paragraph({
            text: "No subsections generated in outline.",
            italics: true,
            spacing: { before: 200 },
          })
        );
      }

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'technical-outline.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Technical Outline exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting Technical Outline to DOCX:', error);
      if (showToast) {
        showToast('Error exporting Technical Outline');
      }
    }
  };

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="border-b pb-2 mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Comprehensive Technical Outline</h3>
          <button
            onClick={exportToDocx}
            className="text-sm px-3 py-1 rounded flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white"
            title="Export outline as Word document"
          >
            <Download className="w-4 h-4" />
            Export DOCX
          </button>
        </div>
      )}

      {/* Main Innovation Card */}
      {mainInnovation && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              Main Innovation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-800 leading-relaxed">{mainInnovation}</p>
            {mainInnovationSection && (
              <div className="mt-3 flex items-center gap-2 text-xs text-amber-700">
                <Star className="w-4 h-4" />
                <span>See detailed section: {mainInnovationSection.subsection}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            Outline Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">{subsections.length}</div>
              <div className="text-xs text-gray-600 mt-1">Subsections</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-indigo-600">
                {subsections.reduce((sum: number, s: any) => sum + (s.content_points?.length || 0), 0)}
              </div>
              <div className="text-xs text-gray-600 mt-1">Total Content Points</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subsections */}
      {subsections.map((subsection: any, index: number) => {
        const isExpanded = expandedSections.has(index);
        const contentPoints = subsection.content_points || [];
        const technicalDepth = subsection.technical_depth || 'unknown';
        const sectionFocus = subsection.section_focus || 'general';
        const isMainInnovation = subsection.is_main_innovation === true;

        return (
          <Card
            key={index}
            className={`overflow-hidden ${isMainInnovation ? 'border-2 border-amber-400 shadow-lg' : ''}`}
          >
            <CardHeader
              className={`cursor-pointer transition-colors ${
                isMainInnovation
                  ? 'bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100'
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => toggleSection(index)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="mt-1">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-bold text-gray-500">#{index + 1}</span>
                      {isMainInnovation && (
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                      )}
                      <h4 className="text-base font-semibold text-gray-900">
                        {subsection.subsection}
                      </h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {isMainInnovation && (
                        <Badge className="text-xs bg-amber-500 text-white border-amber-600">
                          Main Innovation
                        </Badge>
                      )}
                      <Badge variant="outline" className={`text-xs ${getTechnicalDepthColor(technicalDepth)}`}>
                        {technicalDepth}
                      </Badge>
                      <Badge variant="outline" className="text-xs bg-gray-100 text-gray-700 border-gray-200">
                        {sectionFocus}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent className={`pt-0 ${isMainInnovation ? 'bg-amber-50/30' : ''}`}>
                <div className="border-t pt-4 mt-2">
                  <h5 className="text-sm font-semibold text-gray-700 mb-3">
                    Content Points to Cover ({contentPoints.length}):
                  </h5>
                  <div className="space-y-2">
                    {contentPoints.map((point: string, pointIndex: number) => (
                      <div key={pointIndex} className="flex gap-3">
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full text-xs font-medium flex items-center justify-center mt-0.5 ${
                          isMainInnovation
                            ? 'bg-amber-200 text-amber-800'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {pointIndex + 1}
                        </div>
                        <p className="text-sm text-gray-700 flex-1 leading-relaxed">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      {subsections.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No subsections generated in outline.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
